import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { cacheSync } from '@/lib/cacheSync';
import { cachePatch, listPatch, useOptimisticMutation } from '@/lib/optimistic-mutation';
import { useSelectedAcademicYear } from '@/hooks/useSelectedAcademicYear';
import type { Homeroom, Teacher } from '@/types/api';
import { adminKeys, homeroomKeys } from '@/lib/queryKeys'

/**
 * The school's pastoral groups.
 *
 * Its own module rather than living inside `useAttendance`, because it is no
 * longer only attendance's: the roster filters by homeroom and the student form
 * assigns one. A shared list is the point — two copies would be two answers to
 * "which groups exist", and the register would offer one set while the form
 * assigned from another.
 */

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export { homeroomKeys }

/**
 * Narrowed to the academic year in scope rather than listed whole: a school in
 * its second year has last year's groups in the table, and offering them would
 * let a teacher take today's register for a homeroom that no longer meets — or
 * an admin file a new child into one.
 *
 * *Where there is a year in scope.* With none — a school that has configured no
 * academic years, or the moment before the year query settles — the parameter is
 * omitted and every year's groups come back. That is deliberately not "fall back
 * to the newest year": which year an unscoped session means is a product
 * question, and guessing it here would put children into a group nobody chose.
 * The visible cost is duplicate names in the picker ("Koalas" for two years,
 * with nothing to tell them apart), which is why the intake resolves a name to
 * the *active* year's group and calls a genuine tie an error rather than picking
 * one. Worth revisiting when a school first runs two years at once.
 */
export function useHomerooms() {
  const { selectedYear } = useSelectedAcademicYear();
  const academicYearId = selectedYear?.id ?? null;

  return useQuery({
    queryKey: homeroomKeys.list(academicYearId),
    queryFn: async () => {
      const query = academicYearId ? `?academicYearId=${academicYearId}` : '';
      const res = await api.get<{ homerooms: Homeroom[] }>(`/api/homerooms${query}`);
      return res.data.homerooms;
    },
  });
}

/**
 * What the admin screen may set on a group.
 *
 * `academicYearId` is on create and not on update: moving a group between years
 * would take its register history with it, and "the Koalas, but next year" is a
 * new group the school sets up rather than an edit to this one. The API refuses
 * it for the same reason.
 */
export interface HomeroomWrite {
  name: string;
  /** Null is a *statement* — this group is unstaffed — not an omission. */
  teacherId: string | null;
  gradeLevels: number[];
  sortOrder: number;
  anyTeacherCanTakeAttendance: boolean;
}

/**
 * Every mutation invalidates `homeroomKeys.all` rather than the one year's list.
 * The key carries the academic year in scope, and a school that has just added
 * next year's groups will switch to that year expecting to see them — a patch
 * of the list that happens to be loaded would leave the other years stale until
 * a reload.
 *
 * The create does not patch optimistically: a new group comes back with a
 * server-assigned id and a headcount, and a duplicate name is refused — a row
 * that appeared and then vanished would read as a bug. The edit and the delete
 * do, under the conditions each notes below.
 */
export function useCreateHomeroom() {
  return useOptimisticMutation({
    mutationFn: async (data: HomeroomWrite & { academicYearId: string }) => {
      const res = await api.post<{ homeroom: Homeroom }>('/api/homerooms', data);
      return res.data.homeroom;
    },
    invalidateKeys: cacheSync.homerooms(),
  });
}

/**
 * `teacherName` is the server's, denormalised onto the row so the table has
 * something to render — and it is the reason this edit cannot simply be spread
 * over the cached group. A patch that moved `teacherId` alone would leave the
 * table naming the teacher the group has just stopped having.
 *
 * So it is resolved from the staff list the form's own select was filled from.
 * Where that list is not cached the teacher half of the edit is left to the
 * refetch rather than guessed at: a wrong name on screen is worse than a right
 * one a moment late.
 */
function teacherChange(
  queryClient: QueryClient,
  teacherId: string | null
): Partial<Homeroom> {
  if (teacherId === null) return { teacherId: null, teacherName: null };

  const teacher = queryClient
    .getQueryData<Teacher[]>(adminKeys.teachers())
    ?.find((candidate) => candidate.id === teacherId);

  // Built the way `routes/homerooms.ts` builds it, so the prediction is the
  // string the response will carry rather than one that differs by a space.
  return teacher
    ? { teacherId, teacherName: [teacher.firstName, teacher.lastName].filter(Boolean).join(' ') }
    : {};
}

export function useUpdateHomeroom() {
  const queryClient = useQueryClient();

  return useOptimisticMutation({
    mutationFn: async ({ id, data }: { id: string; data: HomeroomWrite }) => {
      const res = await api.put<{ homeroom: Homeroom }>(`/api/homerooms/${id}`, data);
      return res.data.homeroom;
    },
    // The name, the grade levels and the ordering are exactly what the admin
    // typed; `studentCount` is the server's and this edit cannot move it.
    optimistic: ({ id, data: { teacherId, ...data } }) => [
      cachePatch<Homeroom[]>(homeroomKeys.all, (homerooms) =>
        listPatch.update(homerooms, id, { ...data, ...teacherChange(queryClient, teacherId) })
      ),
    ],
    // The students list too, for the same reason the delete does: a student's
    // row carries `homeroomName`, denormalised into the roster response, so an
    // October rename would leave the roster naming a group that no longer goes
    // by that name until something else happened to refetch it. Left to the
    // refetch rather than patched — it is a different page's cache, and the
    // invalidation is awaited, so it has landed before this resolves.
    invalidateKeys: cacheSync.homerooms(),
  });
}

/**
 * Filing children into a group, and taking them out again.
 *
 * The other direction from `useUpdateStudent`, which sets one child's homeroom
 * from the student form. That is the right shape for one new enrolment and the
 * wrong one for the twenty-eight an admin has just created a group for: it means
 * opening twenty-eight records one at a time, or building a CSV. These two drive
 * the membership sheet on the Homerooms page.
 *
 * Neither patches optimistically. Both answer with the group's new *headcount*,
 * counted server-side over roster presence, and the two refusals the API can
 * give — a child who has left, an id from another school — are exactly the cases
 * where a row that moved and then moved back would read as a bug.
 *
 * The roster is invalidated alongside the homeroom list because a student's row
 * carries `homeroomName`: without it the Students page would go on filing these
 * children under their old group, and the sheet itself reads its membership from
 * that same cached roster. So is the register, because who is on tomorrow's roll
 * call is exactly what these two writes move — `cacheSync.homerooms` names all
 * three.
 */
export function useAssignStudentsToHomeroom() {
  return useOptimisticMutation({
    mutationFn: async ({ id, studentIds }: { id: string; studentIds: string[] }) => {
      const res = await api.post<{ homeroom: Homeroom; assigned: number }>(
        `/api/homerooms/${id}/students`,
        { studentIds }
      );
      return res.data;
    },
    invalidateKeys: cacheSync.homerooms(),
  });
}

export function useRemoveStudentsFromHomeroom() {
  return useOptimisticMutation({
    mutationFn: async ({ id, studentIds }: { id: string; studentIds: string[] }) => {
      const res = await api.post<{ homeroom: Homeroom; removed: number }>(
        `/api/homerooms/${id}/students/remove`,
        { studentIds }
      );
      return res.data;
    },
    invalidateKeys: cacheSync.homerooms(),
  });
}

/**
 * Taking children out of whatever group each of them is in.
 *
 * The third membership write, and the only one that names no group. The roster's
 * bulk bar acts on a selection that spans homerooms — an admin ticks six names
 * on a list that shows each child's current group, and "out of a homeroom" is
 * the whole of what they are asking for. `useRemoveStudentsFromHomeroom` cannot
 * say that: it is scoped to one group's members, deliberately, and would leave
 * everyone else in the selection where they are.
 *
 * Answers with what it *changed* rather than what it was asked to change, so the
 * notice afterwards can be honest about a selection that already held children
 * in no group at all.
 */
export function useUnassignStudentsFromHomeroom() {
  return useOptimisticMutation({
    mutationFn: async ({ studentIds }: { studentIds: string[] }) => {
      const res = await api.post<{ removed: number }>('/api/homerooms/unassign', {
        studentIds,
      });
      return res.data;
    },
    invalidateKeys: cacheSync.homerooms(),
  });
}

export function useDeleteHomeroom() {
  return useOptimisticMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/homerooms/${id}`);
    },
    /**
     * Only an empty group goes at once.
     *
     * The API refuses to delete a group that still has children in it, and for
     * this button that refusal is the ordinary path rather than an edge case —
     * the admin is often finding out that way. Taking the row off the table and
     * putting it back a moment later is a worse answer than leaving it there
     * under the toast that explains why. Where the group is empty there is
     * nothing left to refuse, so it goes immediately.
     */
    optimistic: (id) => [
      cachePatch<Homeroom[]>(homeroomKeys.all, (homerooms) => {
        const doomed = homerooms.find((homeroom) => homeroom.id === id);
        if (!doomed || doomed.studentCount > 0) return undefined;
        return listPatch.remove(homerooms, id);
      }),
    ],
    // The roster's homeroom filter and the register's picker both read this
    // list, and a student's row names the group they are in — see
    // `cacheSync.homerooms`.
    invalidateKeys: cacheSync.homerooms(),
  });
}
